/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import type { FieldOption } from '../types';
import { UnstyledButton } from '@/shared/ui';
import {
  useClickOutsideClose,
} from '@/shared/ui/components/form/inputs/Select.helpers';
import {
  useDropdownPosition, dropdownPanelStyle,
} from '@/shared/ui/components/form/inputs/use-dropdown-position';

/** The sentinel the "Ignorar" option carries, since `null` has no DOM value. */
const IGNORE_VALUE = '__ignore__';

export interface FieldChipSelectProps {
  /** The spreadsheet's own column name, for the accessible label. */
  originalName: string;
  /** The Housien field this column lands in, or `null` for ignored. */
  value: string | null;
  options: FieldOption[];
  onChange: (field: string | null) => void;
  /** True while a commit is in flight: remapping a column mid-commit is blocked. */
  disabled?: boolean;
}

/**
 * The pill-shaped select under a column header, naming the Housien field the
 * column feeds.
 *
 * A compact custom trigger + portalled listbox rather than the shared DS
 * `Select`: that component is a full-size form field (44px tall, bordered),
 * which does not fit inside a table header's pill. The portal itself reuses
 * the same viewport-aware positioning `Select`'s own desktop dropdown uses
 * (`useDropdownPosition`), so this sits inside a sticky, horizontally
 * scrolling table header without detaching from its trigger: the position
 * re-measures on every scroll, including a horizontal scroll of the table.
 *
 * @param props - Column name, current field, and the options to choose from.
 * @returns The rendered chip.
 */
export const FieldChipSelect: React.FC<FieldChipSelectProps> = ({
  originalName, value, options, onChange, disabled = false,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const ignored = value === null;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pos = useDropdownPosition(containerRef, open);

  useClickOutsideClose(open, containerRef, () => setOpen(false));

  const selectedOption = options.find((option) => option.value === value);
  const label = ignored ? t('preview.ignoreOption') : (selectedOption?.label ?? value);

  const pick = (nextValue: string) => {
    setOpen(false);
    onChange(nextValue === IGNORE_VALUE ? null : nextValue);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <UnstyledButton
        data-testid={`field-chip-${originalName}`}
        aria-label={t('preview.fieldChipLabel', { orig: originalName })}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          appearance: 'none',
          border: 'none',
          outline: 'none',
          cursor: disabled ? 'default' : 'pointer',
          padding: '4px 24px 4px 10px',
          borderRadius: 'var(--radius-full)',
          fontSize: 12,
          fontWeight: 'var(--font-semibold)' as unknown as number,
          fontFamily: 'var(--font-family)',
          background: ignored ? 'var(--surface-tertiary)' : 'var(--seed-light)',
          color: ignored ? 'var(--text-tertiary)' : 'var(--seed-base)',
          whiteSpace: 'nowrap',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {label}
      </UnstyledButton>
      <ChevronDown
        width={10}
        height={10}
        style={{
          position: 'absolute', right: 8, pointerEvents: 'none',
          color: ignored ? 'var(--text-tertiary)' : 'var(--seed-base)',
        }}
      />
      {open && pos ? createPortal(
        <div
          data-select-portal
          data-testid={`field-chip-${originalName}-listbox`}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className="border border-(--border-subtle) bg-(--surface-secondary) shadow-[var(--shadow-md)] rounded-(--radius-md) overflow-hidden"
          style={dropdownPanelStyle(pos, 10050)}
        >
          {/* flex-col + items-stretch, not w-full on the buttons: a `width:
              100%` child was not resolving to 'auto' for this portal's own
              shrink-to-fit sizing in a real browser (only in automated DOM
              checks), so the whole menu rendered at its `maxWidth` budget
              instead of hugging its longest option. Flex stretch sizes the
              buttons during normal layout, after the container's own width
              is already settled, so it carries none of that ambiguity.
              shrink-0 on every row: default flex-shrink:1 squeezed all 52+
              rows down to fit the 240px max-height instead of scrolling,
              rendering each option a few px tall and the text illegible. */}
          <div className="max-h-60 overflow-auto p-(--space-1) flex flex-col items-stretch">
            {options.map((option) => (
              <UnstyledButton
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                onClick={() => pick(option.value)}
                className="shrink-0 text-left text-sm px-(--space-2) py-(--space-2) rounded-(--radius-sm) hover:bg-[var(--surface-hover)] truncate"
              >
                {option.label}
              </UnstyledButton>
            ))}
            <UnstyledButton
              role="option"
              aria-selected={ignored}
              onClick={() => pick(IGNORE_VALUE)}
              className="shrink-0 text-left text-sm px-(--space-2) py-(--space-2) rounded-(--radius-sm) hover:bg-[var(--surface-hover)]"
            >
              {t('preview.ignoreOption')}
            </UnstyledButton>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
};
